import _ from 'lodash';
import React, { useMemo, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import {
  Button,
  Container,
  ContainerButton,
  ContainerStatements,
  Divider,
  ErrorMessage,
  Link,
  SkipLogicContainer,
  Text,
  Title,
} from './styles';

import {
  SKIP_LOGIC_ALLOWED_QUESTIONS,
  SKIP_LOGIC_NO_QUESTIONS_MESSAGE,
  SKIP_LOGIC_SUBSCRIPTION,
  SKIP_LOGIC_ZERO_STATE,
} from '../../../../../components/question/survey-const';

import Condition from './condition';
import { buildConditionObject, isLabelEmpty } from './helper';

const SkipLogicModal = (props) => {
  /** props */
  const {
    active,
    onClose,
    onSetValue,
    page,
    skipToOptions,
  } = props;

  const { pageName, tasks: questions, skipLogic = {} } = page;
  const { conditions: currentConditions } = skipLogic;

  // Skip logic can only be applied if there are valid questions: range and multiple/single select
  const validQuestions = useMemo(() => {
    const allowedQuestions = _.filter(questions, ({ questionType }) => _.includes(SKIP_LOGIC_ALLOWED_QUESTIONS, questionType));
    const allowedQuestionsWithIndex = _.map(allowedQuestions, (question) => {
      const originalIndex = _.findIndex(questions, { id: question.id });

      return {
        ...question,
        index: originalIndex,
      };
    });

    return allowedQuestionsWithIndex;
  }, [questions]);

  const hasQuestions = !_.isEmpty(validQuestions);

  /** states */
  const [submitting, setSubmitting] = useState(false);
  const [conditions, setConditions] = useState(currentConditions || []);
  const [error, setError] = useState(false);

  useEffect(() => {
    let hasError = false;

    _.forEach(conditions, ({ skipConditions }) => {
      if (hasError) return;

      /**
       * This loop checks if all skip values are filled out
       * Ps.: Maybe we should improve this loop in the future
      */
      _.forEach(skipConditions, ({ questionId, questionOptionId, operation }) => {
        if (hasError) return;

        if (!questionId || !questionOptionId || !operation) {
          hasError = true;
        }

        if (questionId) {
          const questionObj = _.find(validQuestions, { id: questionId });
          const hasEmptyLabel = isLabelEmpty(questionObj?.label)
            || !_.isEmpty(_.filter(questionObj?.questionOptions, ({ label }) => isLabelEmpty(label)));

          if (hasEmptyLabel) {
            hasError = true;
          }
        }
      });
    });

    setError(hasError);
  }, [conditions, validQuestions]);

  // submit field values
  const handleSave = () => {
    if (hasQuestions) {
      setSubmitting(true);

      const newSkipLogic = {
        ...skipLogic,
        conditions,
      };

      onSetValue(newSkipLogic);
    }

    setSubmitting(false);
    onClose();
  };

  const handleCreateCondition = () => {
    const builtSkipLogic = buildConditionObject();
    setConditions([...conditions, builtSkipLogic]);
  };

  const handleUpdateCondition = (id, newCondition) => {
    const newConditions = _.map(conditions, (condition) => {
      if (condition.id === id) {
        return { ...newCondition };
      }

      return { ...condition };
    });

    setConditions(newConditions);
  };

  const handleDeleteCondition = (conditionId) => {
    const updatedConditions = _.filter(conditions, ({ id }) => id !== conditionId);
    setConditions(updatedConditions);
  };

  const renderConditions = () => {
    if (_.isEmpty(conditions)) {
      return (
        <Text padding={20}>{SKIP_LOGIC_ZERO_STATE}</Text>
      );
    }

    const lastConditionId = _.last(conditions).id;

    const options = {
      questions: validQuestions,
      skipToOptions,
    };

    return (
      <>
        {_.map(conditions, (condition) => (
          <div key={condition.id}>
            <Condition
              condition={condition}
              onUpdate={(value) => handleUpdateCondition(condition.id, value)}
              onDelete={() => handleDeleteCondition(condition.id)}
              options={options}
            />

            {lastConditionId !== condition.id && (<Divider />)}
          </div>
        ))}
      </>
    );
  };

  return (
    <Container
      active={active}
      onClickOutside={onClose}
      page
    >
      <SkipLogicContainer>
        <Title>{`Skip logic for ${pageName}`}</Title>

        {!hasQuestions ? (
          <ErrorMessage padding>{SKIP_LOGIC_NO_QUESTIONS_MESSAGE}</ErrorMessage>
        ) : (
          <ContainerStatements>
            <Text padding={20}>
              {SKIP_LOGIC_SUBSCRIPTION}
            </Text>

            {renderConditions()}

            <Link onClick={handleCreateCondition}>
              + Add skip logic statement
            </Link>
          </ContainerStatements>
        )}

        <ContainerButton>
          {error && <ErrorMessage>One or more values is blank</ErrorMessage>}

          <Button
            onClick={() => !error && handleSave()}
            disabled={submitting || error}
            loading={submitting}
          >
            Save
          </Button>
        </ContainerButton>

      </SkipLogicContainer>
    </Container>
  );
};

SkipLogicModal.propTypes = {
  active: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSetValue: PropTypes.func.isRequired,
  skipToOptions: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      text: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    }),
  ).isRequired,
  page: PropTypes.shape({
    id: PropTypes.string.isRequired,
    pageName: PropTypes.string.isRequired,
    tasks: PropTypes.arrayOf(PropTypes.object),
    skipLogic: PropTypes.shape({
      conditions: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string.isRequired,
          surveyPageId: PropTypes.string.isRequired,
          skipConditions: PropTypes.arrayOf(
            PropTypes.shape({
              id: PropTypes.string,
              questionId: PropTypes.string,
              questionOptionId: PropTypes.string,
              operation: PropTypes.string,
            }).isRequired,
          ).isRequired,
        }),
      ),
    }),
  }).isRequired,
};

export default SkipLogicModal;
